import { getPlatform } from "@shared/utils/get-platform";
import { useCallback, useEffect, useRef } from "react";

/**
 * Infinite-scroll hook backed by IntersectionObserver.
 *
 * @param handler - Called when the sentinel intersects. Typically `fetchNextPage`.
 * @param condition - Return `true` when more data should be fetched.
 *   Always use `isFetchingNextPage` (not `isFetching`) from React Query so the
 *   guard is only active while the *next* page is loading, not during the initial fetch.
 * @param options - Forwarded to `IntersectionObserver`. When the sentinel lives inside
 *   a constrained scroll container (modal, drawer, sidebar) you **must** pass
 *   `root` so the observer uses that container instead of the viewport:
 *
 *   ```tsx
 *   const [scrollContainer, setScrollContainer] = useState<HTMLDivElement | null>(null);
 *   const scrollContainerRef = useCallback((node: HTMLDivElement | null) => {
 *     setScrollContainer(node);
 *   }, []);
 *
 *   const { loadReference } = useLoadMore({
 *     handler: fetchNextPage,
 *     condition() { return Boolean(hasNextPage && !isFetchingNextPage); },
 *     options: { root: scrollContainer, rootMargin: '0px' },
 *   });
 *   ```
 *   Use a callback ref + `useState` so that `options.root` is a stable state value
 *   and the effect re-runs after the DOM node mounts.
 */
export default function useLoadMore({
  handler,
  condition,
  options = {},
}: {
  handler(): void;
  condition(): boolean;
  options?: IntersectionObserverInit;
}) {
  const loadReference = useRef<HTMLDivElement>(null);
  const callback = useCallback<IntersectionObserverCallback>(
    (entries) => {
      const target = entries[0];

      if (target.isIntersecting && condition()) {
        handler();
      }
    },
    [condition, handler]
  );

  useEffect(() => {
    const { current } = loadReference;

    if (getPlatform() === "MOBILE") {
      return undefined;
    }

    // IntersectionObserver must be inside a useEffect.
    // This makes it so it's only called when the component using it is mounted,
    // so it guarantees that we are in a browser window.
    // fixes IntersectionObserver is not defined error
    const observer = new IntersectionObserver(
      (entries) => {
        const target = entries[0];

        if (target.isIntersecting && condition()) {
          handler();
        }
      },
      {
        root: null,
        rootMargin: "200px",
        threshold: 0.75,
        ...options,
      }
    );

    if (loadReference.current) {
      observer.observe(loadReference.current);
    }

    return () => {
      if (current) {
        observer.unobserve(current);
      }
    };
  }, [callback, options?.root]);

  return {
    loadReference,
  };
}
